import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button/Button';
import { Icon } from '@/components/ui/Icon/Icon';
import { MarkdownPreview } from '@/components/ui/MarkdownPreview/MarkdownPreview';
import { nb } from '@/i18n/nb';
import s from './MarkdownEditor.module.css';

type MarkdownEditorProps = {
  title: string;
  slug: string;
  content: string;
  version: number;
  updatedByName?: string | null;
  /** Parent flips this on a 409 from the save; disables Lagre + shows the banner. */
  conflict?: boolean;
  saving?: boolean;
  saved?: boolean;
  /** 'display' previews Om hytta in the script font, matching its public view. */
  previewVariant?: 'default' | 'display';
  returnLabel: string;
  onSave: (content: string) => void;
  onReload: () => void;
  onClose: () => void;
};

/*
 * Hand-built markdown editor (ADR-0020): a plain textarea (the source of truth) beside a
 * live react-markdown preview, stacking on mobile. Presentational — the parent owns the
 * document fetch, the save mutation, and the optimistic-concurrency state; this component
 * owns only the in-progress text + the help toggle.
 */
export function MarkdownEditor({
  title,
  slug,
  content,
  version,
  updatedByName,
  conflict = false,
  saving = false,
  saved = false,
  previewVariant = 'default',
  returnLabel,
  onSave,
  onReload,
  onClose,
}: MarkdownEditorProps) {
  const [text, setText] = useState(content);
  const [showHelp, setShowHelp] = useState(false);
  const t = nb.documents.editor;

  // Re-sync the textarea to freshly loaded content when the authoritative version
  // changes (after a save or a reload). Keyed on version, so in-progress typing —
  // which never changes version — is never clobbered mid-edit.
  useEffect(() => {
    setText(content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const dirty = text !== content;

  return (
    <section className={s.editor} role="region" aria-label={`${t.editPrefix} ${title}`}>
      <div className={s.bar}>
        <button type="button" className={s.backBtn} onClick={onClose}>
          <Icon name="back" size={18} /> {returnLabel}
        </button>
        <div className={s.titleWrap}>
          <h1 className={s.title}>
            {t.editPrefix} {title}
          </h1>
          <span className={s.path}>/admin/innhold/{slug}</span>
        </div>
        <span className={s.spacer} />
        {saved && !dirty && !conflict && (
          <span className={s.saved}>
            <Icon name="check" size={15} /> {t.saved}
          </span>
        )}
        <Button variant="primary" onClick={() => onSave(text)} disabled={conflict} loading={saving}>
          {t.save}
        </Button>
      </div>

      {conflict && (
        <div className={s.conflict} role="alert">
          <Icon name="alert" size={20} className={s.conflictIcon} />
          <div className={s.conflictText}>
            <b>{t.conflictTitle}</b>
            <span>{t.conflictBody}</span>
          </div>
          <Button variant="secondary" onClick={onReload}>
            {t.reload}
          </Button>
        </div>
      )}

      <div className={s.split}>
        <div className={s.pane}>
          <div className={s.paneHead}>
            <span className={s.paneLabel}>{t.sourceLabel}</span>
            <button
              type="button"
              className={s.helpLink}
              onClick={() => setShowHelp((h) => !h)}
              aria-expanded={showHelp}
            >
              {t.help}
            </button>
          </div>
          {showHelp && <div className={s.cheat}>{t.cheatSheet}</div>}
          <textarea
            className={s.textarea}
            value={text}
            spellCheck={false}
            onChange={(e) => setText(e.target.value)}
            aria-label={t.sourceAria}
          />
        </div>
        <div className={s.pane}>
          <div className={s.paneHead}>
            <span className={s.paneLabel}>{t.previewLabel}</span>
          </div>
          <div className={s.preview}>
            <MarkdownPreview markdown={text} variant={previewVariant} />
          </div>
        </div>
      </div>

      <div className={s.foot}>
        <span className={s.meta}>
          {t.basedOn} {version}
          {updatedByName ? ` · ${t.lastEditedBy} ${updatedByName}` : ''}
        </span>
      </div>
    </section>
  );
}
